import React, { useMemo } from 'react';
import moment from 'moment';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList, ReferenceLine,
} from 'recharts';

// Class Data for BCA. Scores only: BCA questions carry no categories, so there
// is no category breakdown, accuracy over time or focus-area view.
// Scores are +2 per right answer, -0.5 per wrong one, 0 per blank: -20 to 80.

type BCATestData = {
    testName: string;
    testDate: string;
    avgScore: number | null;
    bestScore: number | null;
    lowestScore: number | null;
    minScore: number;
    maxScore: number;
    takenNum: number;
    assignedNum: number;
    avgCorrect: number | null;
    avgWrong: number | null;
    avgBlank: number | null;
    studentScores: {
        studentId: number;
        firstName: string;
        lastName: string;
        score: number;
        correct: number;
        wrong: number;
        blank: number;
    }[];
};

type BCAPerformanceHistoryProps = {
    tests: BCATestData[];
};

const mean = (nums: number[]) => (nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : null);
const oneDecimal = (n: number | null) => (n == null ? '-' : Math.round(n * 10) / 10);

function BCAPerformanceHistory({ tests }: BCAPerformanceHistoryProps) {
    // A test can be assigned to a class more than once; keep one card per test
    // name, the one with the most completions (newest-first order preserved).
    const testData = useMemo(() => {
        const best = new Map<string, BCATestData>();
        tests.forEach(t => {
            const cur = best.get(t.testName);
            if (!cur || t.takenNum > cur.takenNum) best.set(t.testName, t);
        });
        return Array.from(new Set(tests.map(t => t.testName))).map(name => best.get(name)!);
    }, [tests]);

    const takenTests = testData.filter(t => t.takenNum > 0);
    const latest = takenTests[0] || testData[0];

    // oldest -> newest so the chart reads left to right
    const chartData = takenTests.slice().reverse().map(t => ({
        testName: t.testName.replace(/^BCA\s*/, ''),
        assignedDate: t.testDate,
        avgScore: t.avgScore,
    }));

    const renderTestTick = ({ x, y, payload }: any) => {
        const row = chartData.find(p => p.testName === payload.value);
        const dateStr = row?.assignedDate ? moment(row.assignedDate).format('MMM D, YYYY') : '';
        return (
            <g transform={`translate(${x},${y})`}>
                <g transform="rotate(-28)">
                    <text x={0} y={0} dy={6} textAnchor="end" fontSize={11} fill="#313131">{payload.value}</text>
                    {dateStr ? <text x={0} y={0} dy={18} textAnchor="end" fontSize={9} fill="#98A2B3">{dateStr}</text> : null}
                </g>
            </g>
        );
    };

    if (!latest) {
        return (
            <div className="cdr__tab-content">
                <div className="cdr__empty">No test data for this test type yet.</div>
            </div>
        );
    }

    const classAvg = mean(takenTests.map(t => t.avgScore).filter((v): v is number => v != null));
    const bestEver = takenTests.length ? Math.max(...takenTests.map(t => t.bestScore ?? -Infinity)) : null;
    const latestStudents = (latest.studentScores || []).slice().sort((a, b) => b.score - a.score);

    return (
        <div className="cdr__tab-content">
            {/* Latest test summary */}
            <div className="cdr__card">
                <div className="cdr__card-sub">Latest Test</div>
                <div className="cdr__summary-name">{latest.testName}</div>
                <div className="cdr__summary-date">
                    {moment(latest.testDate).format('MMMM D, YYYY')} · {latest.takenNum} of {latest.assignedNum} students completed
                </div>
                <div className="cdr__stat-grid">
                    <div className="cdr__stat">
                        <div className="cdr__stat-label">Class avg</div>
                        <div className="cdr__stat-val">{oneDecimal(latest.avgScore)}</div>
                        <div className="cdr__stat-range">{latest.minScore} to {latest.maxScore}</div>
                    </div>
                    <div className="cdr__stat cdr__stat--divider">
                        <div className="cdr__stat-label">Best</div>
                        <div className="cdr__stat-val cdr__stat-val--sm">{oneDecimal(latest.bestScore)}</div>
                    </div>
                    <div className="cdr__stat cdr__stat--divider">
                        <div className="cdr__stat-label">Lowest</div>
                        <div className="cdr__stat-val cdr__stat-val--sm">{oneDecimal(latest.lowestScore)}</div>
                    </div>
                    <div className="cdr__stat cdr__stat--divider">
                        <div className="cdr__stat-label">Avg correct</div>
                        <div className="cdr__stat-val cdr__stat-val--sm">{oneDecimal(latest.avgCorrect)}</div>
                    </div>
                    <div className="cdr__stat cdr__stat--divider">
                        <div className="cdr__stat-label">Avg incorrect</div>
                        <div className="cdr__stat-val cdr__stat-val--sm">{oneDecimal(latest.avgWrong)}</div>
                    </div>
                    <div className="cdr__stat cdr__stat--divider">
                        <div className="cdr__stat-label">Avg omitted</div>
                        <div className="cdr__stat-val cdr__stat-val--sm">{oneDecimal(latest.avgBlank)}</div>
                    </div>
                </div>
            </div>

            {/* Class averages across every BCA test taken */}
            {takenTests.length > 0 && <div className="cdr__kpi-caption">Across all tests taken · class average</div>}
            <div className="cdr__kpis">
                <div className="cdr__kpi">
                    <div className="cdr__kpi-label">Class avg</div>
                    <div className="cdr__kpi-val">{oneDecimal(classAvg)}</div>
                </div>
                <div className="cdr__kpi">
                    <div className="cdr__kpi-label">Best score</div>
                    <div className="cdr__kpi-val">{oneDecimal(bestEver)}</div>
                </div>
                <div className="cdr__kpi">
                    <div className="cdr__kpi-label">Tests taken</div>
                    <div className="cdr__kpi-val">{takenTests.length}</div>
                </div>
            </div>

            {/* Class average per test over time */}
            <div className="cdr__card">
                <div className="cdr__card-head">
                    <div>
                        <div className="cdr__card-title">Class average</div>
                        <div className="cdr__card-sub">Average score per test over time (−20 to 80)</div>
                    </div>
                </div>
                <div style={{ width: '100%', height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{ top: 24, right: 20, left: 0, bottom: 16 }}>
                            <CartesianGrid strokeDasharray="3 4" vertical={false} stroke="#EAECF0" />
                            <XAxis dataKey="testName" tickLine={false} axisLine={{ stroke: '#EAECF0' }} fontSize={12} interval={0} height={72} tick={renderTestTick} />
                            <YAxis domain={[latest.minScore, latest.maxScore]} tickLine={false} axisLine={false} fontSize={12} width={48} />
                            <ReferenceLine y={0} stroke="#98A2B3" />
                            <Tooltip cursor={{ fill: '#0575a209' }} />
                            <Bar dataKey="avgScore" name="Class avg" fill="#0575a2" radius={[3, 3, 0, 0]} maxBarSize={72}>
                                <LabelList dataKey="avgScore" position="top" fontSize={12} fontWeight={700} fill="#313131" />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Every student's result on the latest test */}
            <div className="cdr__card cdr__card--flush">
                <div className="cdr__card-head">
                    <div>
                        <div className="cdr__card-title">Student scores</div>
                        <div className="cdr__card-sub">{latest.testName}</div>
                    </div>
                </div>
                <table className="cdr__tbl">
                    <thead>
                        <tr>
                            <th style={{ width: '40%' }}>Name</th>
                            <th>Score</th>
                            <th>Correct</th>
                            <th>Incorrect</th>
                            <th>Omitted</th>
                        </tr>
                    </thead>
                    <tbody>
                        {latestStudents.length === 0
                        ?   <tr><td colSpan={5} className="cdr__tbl-empty">No completed attempts yet.</td></tr>
                        :   latestStudents.map(s => (
                                <tr key={s.studentId}>
                                    <td>{s.firstName} {s.lastName}</td>
                                    <td>{s.score}</td>
                                    <td>{s.correct}</td>
                                    <td>{s.wrong}</td>
                                    <td>{s.blank}</td>
                                </tr>
                            ))
                        }
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default BCAPerformanceHistory;
